<script lang="ts">
  import { defineComponent, inject, onMounted, ref } from "vue";

  import {
    SettingsManager,
    MinimalEntity,
  } from "frontend/uses/settings/manager/settings-manager";

  import BaseButton from "frontend/components/base/BaseButton.vue";
  import FilterContainer from "frontend/components/filter/FilterContainer.vue";
  import BaseAccordion from "frontend/components/base/BaseAccordion.vue";

  export default defineComponent({
    components: { BaseButton, FilterContainer, BaseAccordion },
    setup() {
      const settingsManager = inject(
        "settings-manager"
      ) as SettingsManager<MinimalEntity>;
      onMounted(() => {
        settingsManager.refreshEntities();
      });

      const onRefreshNeeded = () => {
        settingsManager.refreshEntities();
      };
      const onRefreshArchive = () => {
        settingsManager.refreshEntities();
        settingsManager.refreshArchivedEntities();
      };

      const entityIsOpen = ref<boolean>(false);
      const changeEntity = (accordionIsOpen: boolean) => {
        entityIsOpen.value = accordionIsOpen;
      };
      return {
        entities: settingsManager.entitiesR(),
        archivedEntities: settingsManager.archivedEntities(),

        addNewEntry: () => {
          settingsManager.addNewEntity();
          entityIsOpen.value = true;
        },

        settingsManager,
        entityIsOpen,
        changeEntity,
        onRefreshNeeded,
        onRefreshArchive,
      };
    },
  });
</script>

<template>
  <div class="settings-container" v-on:dragover.prevent v-on:dragenter.prevent>
    <template v-if="settingsManager">
      <FilterContainer
        v-if="settingsManager.enableFilter()"
        v-bind:filter-manager="settingsManager.getFilterManager()"
      />
      <div v-for="entity in entities" v-bind:key="entity.id">
        <component
          v-bind:is="settingsManager.componentForEdit()"
          v-bind:entity="entity"
          v-bind:draggable="
            !entityIsOpen && settingsManager.enableDragAndDrop()
          "
          v-bind:class="{
            draggable: !entityIsOpen && settingsManager.enableDragAndDrop(),
          }"
          v-on:dragstart="settingsManager.onDrag(entity)"
          v-on:drop.stop="settingsManager.onDrop(entity)"
          v-on:entity-open="changeEntity"
          v-on:refresh-needed="onRefreshNeeded"
          v-on:refresh-archive="onRefreshArchive"
        ></component>
      </div>
      <div
        v-if="settingsManager.enableNewEntities()"
        class="settings-container__new-entry"
      >
        <BaseButton v-on:submit="addNewEntry">
          {{ settingsManager.entityName() }} hinzufügen
        </BaseButton>
      </div>

      <BaseAccordion
        v-if="settingsManager.enableArchive()"
        header="Archiv"
        class="settings-container__archive"
        v-on:update:model-value="settingsManager.refreshArchivedEntities(false)"
      >
        <template v-if="archivedEntities.length">
          <component
            v-bind:is="settingsManager.componentForEdit()"
            v-for="entity in archivedEntities"
            v-bind:key="entity.id"
            v-bind:entity="entity"
            v-on:refresh-archive="onRefreshArchive"
          />
        </template>
        <div v-else>Das Archiv ist leer.</div>
      </BaseAccordion>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  @use "frontend/styles/mixins/drag";

  .settings-container__new-entry {
    margin-top: 35px;
  }

  .draggable {
    @include drag.draggable;
  }

  .settings-container__archive :deep(.accordion__header > div:first-child) {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    margin: 20px 0;
  }
</style>
